import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { InlineSpinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Send, Search, Info, AlertTriangle, Copy, Wand2 } from "lucide-react";

const FMT_LABEL = { email: "Email", text: "Text Draft", internal_message: "Internal Message", general_answer: "General Answer" };

export default function CommunicationsCopilot() {
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [state, setState] = useState("");
  const [format, setFormat] = useState("email");
  const [purpose, setPurpose] = useState("recruiting");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/copilot/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/copilot/recipients?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data.items)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const pick = async (p) => {
    setRecipient(p); setResults([]); setQ("");
    try {
      const { data } = await api.get(`/copilot/context?person_id=${p.id}`);
      setCtx(data);
      if (data.applicable_state) setState(data.applicable_state);
    } catch (e) { toast.error(apiError(e)); }
  };

  const generate = async (extra = "") => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/copilot/generate", {
        person_id: recipient?.id, format, purpose, applicable_state: state,
        prompt: (prompt + " " + extra).trim(), sender_profile: ctx?.sender_profile,
      });
      setResult(data);
      if (data.blocked) toast.error(data.message || "Applicable State required");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const send = async () => {
    try {
      const d = result?.draft || {};
      const { data } = await api.post("/copilot/send", {
        person_id: recipient?.id, purpose, applicable_state: state, subject: d.subject,
        mode: result?.mode, answer_block_ids: (result?.information_used || []).map((i) => i.id),
        idempotency_key: `${recipient?.id}-${Date.now()}`,
      });
      if (data.needs_microsoft) toast.message("Connect Microsoft to send — draft saved.");
      else if (data.sent) toast.success("Email sent and logged to the timeline.");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!meta) return <div className="py-16 flex justify-center"><InlineSpinner /></div>;
  const conflict = ctx?.state_conflict;
  const d = result?.draft;

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="copilot-page">
      <div className="flex items-center gap-2 mb-5">
        <Sparkles className="w-6 h-6 text-[#0D9488]" />
        <h1 className="font-display text-2xl font-bold text-slate-900">Communications Copilot</h1>
      </div>

      {/* CONTEXT */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5" data-testid="copilot-context">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">Communication Context</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="relative">
            <label className="text-xs text-slate-500">Recipient</label>
            <div className="flex items-center gap-2 mt-1 border rounded-md px-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input value={recipient ? recipient.name : q} onChange={(e) => { setRecipient(null); setQ(e.target.value); }}
                placeholder="Search people…" className="h-9 flex-1 outline-none text-sm" data-testid="copilot-recipient-input" />
            </div>
            {results.length > 0 && (
              <div className="absolute z-20 bg-white border rounded-md mt-1 w-full shadow-lg max-h-56 overflow-auto" data-testid="copilot-recipient-results">
                {results.map((p) => (
                  <button key={p.id} onClick={() => pick(p)} data-testid={`copilot-recipient-${p.id}`} className="block w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                    <span className="font-medium">{p.name}</span> <span className="text-slate-400">· {p.classification || "Person"} · {p.state || "—"}{p.program ? ` · ${p.program}` : ""}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-500">Applicable State</label>
            <Input value={state} onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))} placeholder="e.g. CA" className="h-9 mt-1" data-testid="copilot-state" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Format</label>
            <Select value={format} onValueChange={setFormat}><SelectTrigger className="h-9 mt-1" data-testid="copilot-format"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{meta.formats.map((f) => <SelectItem key={f} value={f}>{FMT_LABEL[f]}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Purpose</label>
            <Select value={purpose} onValueChange={setPurpose}><SelectTrigger className="h-9 mt-1" data-testid="copilot-purpose"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{meta.purposes.map((p) => <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        {ctx?.recipient && (
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            {ctx.recipient.classification && <Badge variant="outline">{ctx.recipient.classification}</Badge>}
            {ctx.recipient.program && <Badge variant="outline">{ctx.recipient.program}</Badge>}
            {ctx.recipient.stage && <Badge variant="outline">{ctx.recipient.stage}</Badge>}
            {ctx.recipient.assigned_recruiter && <Badge variant="outline">Recruiter: {ctx.recipient.assigned_recruiter}</Badge>}
          </div>
        )}
        {conflict && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="copilot-state-conflict">
            <p className="font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Which state does this communication apply to?</p>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => setState(String(conflict.opportunity).toUpperCase().slice(0, 2))} data-testid="copilot-state-opp">{conflict.opportunity} (Opportunity)</Button>
              <Button size="sm" variant="outline" onClick={() => setState(String(conflict.residence).toUpperCase().slice(0, 2))} data-testid="copilot-state-res">{conflict.residence} (Residence)</Button>
            </div>
          </div>
        )}
      </div>

      {/* QUICK ACTIONS + PROMPT */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(meta.quick_actions[purpose] || []).map((qa) => (
            <button key={qa} onClick={() => setPrompt(qa)} data-testid={`copilot-qa-${qa.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
              className="text-xs rounded-full border border-slate-200 px-2.5 py-1 text-slate-600 hover:border-[#0D9488] hover:text-[#0D9488]">{qa}</button>
          ))}
        </div>
        <label className="text-sm font-medium text-slate-700">What would you like to communicate?</label>
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} className="mt-1" data-testid="copilot-prompt"
          placeholder="e.g. Explain Retail Acquisition and invite them to schedule a meeting." />
        <div className="flex justify-end mt-3">
          <Button onClick={() => generate()} disabled={busy || !prompt} data-testid="copilot-generate" className="bg-[#0D9488] hover:bg-[#0b7d73]">
            {busy ? <InlineSpinner /> : <><Wand2 className="w-4 h-4 mr-2" />Generate</>}
          </Button>
        </div>
      </div>

      {/* PREVIEW */}
      {result && !result.blocked && d && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="copilot-preview">
          <div className="flex items-center gap-2 mb-3">
            <Badge className={result.mode === "no_ai" ? "bg-slate-700 text-white border-0" : "bg-[#0D9488] text-white border-0"} data-testid="copilot-mode">
              {result.mode === "no_ai" ? "No-AI (0 LLM calls)" : result.mode === "standard_ai" ? "Standard AI · Gemini 3 Flash" : result.mode}
            </Badge>
            <Badge variant="outline" data-testid="copilot-confidence">{(result.confidence || "").replace("_", " ")}</Badge>
            {state && <Badge variant="outline">State: {state}</Badge>}
          </div>
          {d.subject !== undefined && <Input value={d.subject || ""} readOnly className="mb-2 font-medium" data-testid="copilot-subject" />}
          <Textarea readOnly rows={10} data-testid="copilot-body"
            value={d.body || d.message || d.answer || ""} className="text-sm" />
          {d.signature && <pre className="text-xs text-slate-500 mt-2 whitespace-pre-wrap">{d.signature}</pre>}

          {result.review_suggested && result.conflict && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="copilot-conflict">
              <p className="font-semibold text-amber-800 flex items-center gap-1.5 text-sm"><AlertTriangle className="w-4 h-4" />{result.conflict.label || "Review Suggested / Sources Disagree"}</p>
              <p className="text-xs text-amber-700 mt-1">{result.conflict.note}</p>
              <p className="text-xs text-amber-700 mt-1">
                Preferred: <b>{result.conflict.prefer === "certified" ? (result.conflict.certified?.name) : (result.conflict.internal?.title)}</b>
                {" · "}Flagged for review: {result.conflict.flag_for_review === "certified" ? result.conflict.certified?.name : result.conflict.internal?.title}
              </p>
              <p className="text-[11px] text-amber-600 mt-1">Both sources are preserved above. Review before sending — nothing is auto-resolved or auto-sent.</p>
            </div>
          )}
          {result.information_used?.length > 0 && (
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid="copilot-info-used">
              <p className="text-[11px] font-semibold text-slate-500 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" />Information used</p>
              <ul className="mt-1 text-xs text-slate-600 space-y-0.5">
                {result.information_used.map((i) => <li key={i.id}>• {i.title} <span className="text-slate-400">({i.type} · {i.domain || i.state})</span></li>)}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            <Button size="sm" variant="outline" onClick={() => generate("make it friendlier")} data-testid="copilot-friendlier">Friendlier</Button>
            <Button size="sm" variant="outline" onClick={() => generate("make it shorter")} data-testid="copilot-shorter">Shorter</Button>
            <Button size="sm" variant="outline" onClick={() => generate("make it more professional")}>More Professional</Button>
            {format === "text" && <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(d.message || ""); toast.success("Copied"); }} data-testid="copilot-copy"><Copy className="w-3.5 h-3.5 mr-1" />Copy Text</Button>}
            {(format === "email") && <Button size="sm" onClick={send} data-testid="copilot-send" className="bg-[#006090] hover:bg-[#004E75] ml-auto"><Send className="w-3.5 h-3.5 mr-1" />Send</Button>}
          </div>
        </div>
      )}
      {result?.blocked && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-amber-800" data-testid="copilot-blocked">
          <AlertTriangle className="w-5 h-5 inline mr-2" />{result.message}
        </div>
      )}
    </div>
  );
}
